import { useState, useEffect } from "react";
import { Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PlateMindLogo } from "@/components/PlateMindLogo";
import { 
  Users, Building2, Bell, BarChart3, Shield, LogOut, Stethoscope, UserCog, Briefcase
} from "lucide-react";
import { toast } from "sonner";
import { LogoutConfirmDialog } from "@/components/ConfirmDialogs";

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  useEffect(() => {
    const checkAdminAndFetchStats = async () => {
      // Check if user is admin
      if (!user || user.role !== "admin") {
        toast.error("Admin access required");
        navigate("/dashboard");
        return;
      }

      try {
        const response = await fetch(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        } else if (response.status === 403) {
          toast.error("Admin access required");
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Admin stats error:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchStats();
  }, [user, token, navigate]);

  const navItems = [
    { path: "/admin", label: t('admin.overview'), icon: BarChart3, exact: true },
    { path: "/admin/users", label: t('admin.users'), icon: Users },
    { path: "/admin/partners", label: t('admin.partners'), icon: Building2 },
    { path: "/admin/private-physicians", label: t('admin.private_physicians'), icon: UserCog },
    { path: "/admin/marketplace", label: t('admin.marketplace'), icon: Stethoscope },
    { path: "/admin/corporate", label: "Corporate Wellness", icon: Briefcase },
    { path: "/admin/notifications", label: t('admin.notifications'), icon: Bell },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="sticky top-0 bg-secondary text-white px-4 py-3 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-bold">{t('admin.dashboard')}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="compact" className="text-white [&_svg]:text-white [&_span]:text-white" />
            <PlateMindLogo size={32} />
            <span className="font-heading font-bold hidden sm:inline">platemind</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLogoutDialog(true)}
              className="text-white/70 hover:text-white hover:bg-white/10"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
        {/* Admin Navigation */}
        <nav className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide" style={{WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-full whitespace-nowrap transition-colors text-xs sm:text-sm ${
                  active
                    ? "bg-primary text-white"
                    : "bg-orange-50 text-secondary hover:bg-orange-100"
                }`}
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className={active ? "" : "hidden sm:inline"}>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Stats Cards (shown on all admin pages) */}
        {stats && location.pathname === "/admin" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <Card className="p-4 bg-white border border-orange-100">
              <p className="text-sm text-muted-foreground">{t('admin.total_users')}</p>
              <p className="text-2xl font-bold text-secondary">{stats.users.total}</p>
            </Card>
            <Card className="p-4 bg-white border border-orange-100">
              <p className="text-sm text-muted-foreground">{t('admin.premium')}</p>
              <p className="text-2xl font-bold text-primary">{stats.users.premium}</p>
            </Card>
            <Card className="p-4 bg-white border border-orange-100">
              <p className="text-sm text-muted-foreground">{t('admin.trial')}</p>
              <p className="text-2xl font-bold text-warning">{stats.users.trial}</p>
            </Card>
            <Card className="p-4 bg-white border border-orange-100">
              <p className="text-sm text-muted-foreground">{t('admin.partner')}</p>
              <p className="text-2xl font-bold text-success">{stats.users.partner}</p>
            </Card>
            <Card className="p-4 bg-white border border-orange-100">
              <p className="text-sm text-muted-foreground">{t('admin.dau')}</p>
              <p className="text-2xl font-bold text-secondary">{stats.engagement.daily_active_users}</p>
            </Card>
            <Card className="p-4 bg-white border border-orange-100">
              <p className="text-sm text-muted-foreground">{t('admin.retention')}</p>
              <p className="text-2xl font-bold text-secondary">{stats.retention.seven_day}%</p>
            </Card>
          </div>
        )}

        {/* Main Content Area */}
        <Outlet context={{ stats, setStats }} />
      </div>

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={() => {
          logout();
          navigate("/login?type=professional");
        }}
      />
    </div>
  );
};

export default AdminLayout;
